import React from 'react';

import images from 'assets/images';
import PageLayout from 'components/common/PageLayout';
import { ENTERPRISE_CONTACT_LINK } from 'components/constants';

import styles from './Security.module.scss';

const securityLayers = [
  {
    number: '01',
    title: 'Identity and authentication',
    text: 'Use single sign-on, two-step verification and organization-level password policies to protect account access.',
    details: [
      'Enterprise SSO with OIDC-compatible identity providers',
      'SSO-only or hybrid access models',
      'Two-step verification by email, SMS or an authenticator app',
      "Support for your organization's authenticator, Google Authenticator and Microsoft Authenticator",
      'Dedicated organization login pages and security logs',
    ],
  },
  {
    number: '02',
    title: 'Roles and permissions',
    text: 'Limit access by organization, team and product so each person can work only with the tools and data they need.',
    details: [
      'Role-based access control',
      'Product and feature permissions',
      'Separate team and group access',
      'Login, security and data-download audit logs',
    ],
  },
  {
    number: '03',
    title: 'Data control',
    text: 'Choose whether collected data is stored in Storycards or transferred directly to systems your organization controls.',
    details: [
      'Direct data transfer with Data Bridge',
      'Customer-controlled destinations',
      'Deletion controls for stored data',
      'Consent records for configured form fields',
    ],
  },
  {
    number: '04',
    title: 'Application and infrastructure',
    text: 'Protect published products and platform traffic with encrypted connections, edge controls and continuous monitoring.',
    details: [
      'TLS 1.2+ in transit',
      'AES-256 encryption at rest',
      'Cloudflare and AWS WAF protection',
      'Private file uploads with malware scanning',
    ],
  },
];

const platformControls = [
  {
    title: 'Managed cloud publishing',
    text: 'Publish interactive products without creating and maintaining a separate hosting environment.',
  },
  {
    title: 'Encrypted data paths',
    text: 'Storycards uses HTTPS with TLS 1.2+ for data in transit and AES-256 encryption for data stored in the platform.',
  },
  {
    title: 'Protected application edge',
    text: 'Cloudflare and AWS Web Application Firewall (AWS WAF) apply layered filtering across the product, while DDoS protection helps absorb disruptive traffic.',
  },
  {
    title: 'Real-time monitoring',
    text: 'Platform activity and infrastructure are monitored 24/7 to surface potential threats and support rapid investigation and mitigation.',
  },
  {
    title: 'Secure account access',
    text: 'Apply SSO-only or hybrid access, two-step verification and password requirements that fit the way your organization manages identity.',
  },
  {
    title: 'Granular workspace access',
    text: 'Define who can create, edit, publish or work with collected data across teams and products.',
  },
];

const technicalAssurance = [
  {
    title: 'Cloud infrastructure',
    text: 'Storycards runs its managed platform on AWS, with Cloudflare protecting and accelerating traffic at the application edge.',
  },
  {
    title: 'Layered WAF and intelligent filtering',
    text: 'Cloudflare and AWS Web Application Firewall (AWS WAF) protect multiple layers of the product. Machine-learning-assisted traffic analysis and managed filtering help identify malicious or automated activity while legitimate requests continue.',
  },
  {
    title: 'DDoS resilience',
    text: 'DDoS protection helps absorb and filter disruptive traffic while managed infrastructure supports continued delivery of published products.',
  },
  {
    title: 'Real-time monitoring',
    text: 'Monitoring runs 24/7 across platform activity and infrastructure so the team can detect, investigate and mitigate potential threats quickly.',
  },
  {
    title: 'Federated identity and login controls',
    text: 'Use SSO with Google Workspace, Microsoft Entra ID, Okta, OneLogin, Auth0, JumpCloud or another OIDC-compatible provider. Organizations can require SSO-only access or keep password access for approved external collaborators.',
  },
  {
    title: 'Account security and audit trails',
    text: 'Dedicated organization login pages, records of successful and failed login attempts, automated IP lockouts, password-expiration policies and data-download logs give administrators visibility into sensitive activity.',
  },
  {
    title: 'Secure file processing',
    text: 'Uploaded files are held in a private, isolated environment and automatically scanned for viruses and malware before they become available. Access is limited by organization permissions, and authorized users can delete files from the platform.',
  },
  {
    title: 'Data location and routing',
    text: 'The data path follows the operating model you choose. Collected data can remain in Storycards or move directly to infrastructure your organization controls through Data Bridge.',
  },
  {
    title: 'Privacy operations',
    text: 'Storycards can record the UTC timestamp, IP address and state of configured consent fields for each submission. Administrators can review, export and delete collected records through controlled platform workflows.',
  },
  {
    title: 'Backup and recovery',
    text: 'Backups and recovery procedures protect platform data stored by Storycards. Retention and recovery requirements can be reviewed for the selected enterprise use case.',
  },
  {
    title: 'Encryption',
    text: 'HTTPS with TLS 1.2+ protects data in transit, while AES-256 protects data stored in the platform.',
  },
];

const independentAssurance = [
  {
    label: 'External validation',
    title: 'Penetration testing twice a year',
    text: 'Independent cybersecurity firms perform penetration testing every six months. Findings are reviewed, prioritized and tracked through remediation.',
  },
  {
    label: 'Compliance program',
    title: 'SOC 2 program in progress',
    text: 'Storycards is preparing for a SOC 2 examination and is implementing controls aligned with the AICPA Trust Services Criteria.',
  },
  {
    label: 'Privacy and governance',
    title: 'Data-processing terms for enterprise teams',
    text: 'Storycards applies a Privacy by Design approach. Our data-processing terms address processing roles, subprocessors, international transfers, deletion and cooperation in the event of a data incident.',
  },
  {
    label: 'Due diligence',
    title: 'A review path for technical stakeholders',
    text: 'Review architecture, deployment, data flow and relevant security controls with our enterprise team. Sensitive materials can be handled through the appropriate review process.',
  },
];

const reviewTopics = [
  {
    title: 'Architecture and deployment',
    text: 'Review how Storycards products are hosted, published or embedded in your existing digital environment.',
  },
  {
    title: 'Data flow and storage',
    text: 'Map what is collected, where it is sent, whether it is stored and which system remains the source of truth.',
  },
  {
    title: 'Identity and access model',
    text: 'Align SSO, two-step verification, password requirements, roles and permissions with your internal policies.',
  },
  {
    title: 'Application protection',
    text: 'Walk through encryption, certificates, traffic filtering, DDoS protection and monitoring controls.',
  },
];

const questions = [
  {
    question: 'Can collected data go directly to our systems?',
    answer:
      'Yes. Data Bridge can transfer collected data directly to your server or database without storing that collected data in the Storycards cloud.',
  },
  {
    question: 'Can we store collected data in Storycards?',
    answer:
      'Yes. When you choose Storycards cloud storage, your organization controls the data and can delete it when it is no longer needed.',
  },
  {
    question: 'How do we control access for our teams?',
    answer:
      'Organizations can use SSO, two-step verification by email, SMS or an authenticator app, configurable password requirements and role-based permissions across products, features and data. Authenticator options include your organization’s solution, Google Authenticator and Microsoft Authenticator.',
  },
  {
    question: 'How are published products protected?',
    answer:
      'Published products use SSL certificates and HTTPS with TLS 1.2+. Cloudflare and AWS WAF apply layered traffic filtering, including machine-learning-assisted protections, alongside DDoS protection and 24/7 monitoring.',
  },
  {
    question: 'Which identity providers can we use for SSO?',
    answer:
      'Storycards supports Google Workspace, Microsoft Entra ID, Okta, OneLogin, Auth0, JumpCloud and other OpenID Connect-compatible identity providers. Organizations can enforce SSO-only access or use a hybrid model for approved external collaborators.',
  },
  {
    question: 'How are uploaded files protected?',
    answer:
      'Files are uploaded into a private, isolated environment and scanned automatically for viruses and malware before they become available. Only authorized organization users can access them, and files can be deleted through the platform.',
  },
  {
    question: 'Does Storycards AI generate or inject application code?',
    answer:
      'No. Storycards AI assembles production-tested Storycards components configured to your design system and brand rules. AI-created products remain inside the same permission, review and publishing controls as products created manually.',
  },
];

const Security: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Enterprise security</p>
            <h1>Build and publish without giving up control.</h1>
            <p className={styles.heroText}>
              Storycards gives enterprise teams one governed environment for creating, publishing and operating
              interactive products. Control who can work in the platform, how products are protected and where collected
              data goes.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Plan a security review <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <aside className={styles.heroPromise} aria-label="Security overview">
            <span>Security at every layer</span>
            <div>
              <strong>Control access</strong>
              <p>Apply identity policies and role-based permissions across teams and products.</p>
            </div>
            <div>
              <strong>Choose the data path</strong>
              <p>Keep data in Storycards or send it directly to systems your organization controls.</p>
            </div>
            <div>
              <strong>Protect what you publish</strong>
              <p>Use managed infrastructure with encrypted connections and application-layer protection.</p>
            </div>
          </aside>
        </div>

        <figure className={styles.heroVisual}>
          <img src={images.securityHero} alt="Storycards security and enterprise data controls" />
          <figcaption>
            <span>Governed workspace</span>
            <span>Protected publishing</span>
            <span>Controlled data flow</span>
          </figcaption>
        </figure>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>Control the product, the people and the data path.</h2>
          <div>
            <p>
              Security in Storycards starts with clear boundaries. Your organization defines who can create, edit,
              publish and access data. Storycards protects the platform and published products with infrastructure,
              application and monitoring controls.
            </p>
            <p>
              This model gives business teams room to create while security, IT and data teams retain the controls
              needed to operate within organizational policy.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.layersSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Security model</p>
              <h2>Four layers of enterprise control.</h2>
            </div>
            <p>
              Each layer answers a different part of the security review, from account access to the destination of
              collected first-party data.
            </p>
          </header>

          <div className={styles.layerGrid}>
            {securityLayers.map((layer) => (
              <article key={layer.title}>
                <span>{layer.number}</span>
                <h3>{layer.title}</h3>
                <p>{layer.text}</p>
                <ul>
                  {layer.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.dataSection}>
        <div className={styles.sectionInner}>
          <header className={styles.dataHeader}>
            <p className={styles.eyebrow}>Data ownership and routing</p>
            <h2>Decide where collected data lives.</h2>
            <p>
              Storycards supports two operating models. Choose the path that fits the sensitivity of the use case and
              the systems your organization already governs.
            </p>
          </header>

          <div className={styles.dataPaths}>
            <article>
              <span>01</span>
              <div>
                <p className={styles.pathLabel}>Direct transfer</p>
                <h3>Route data into your own environment.</h3>
                <p>
                  Use Data Bridge to send collected data directly to your server or database without storing that
                  collected data in the Storycards cloud. Your systems apply the storage, retention and downstream
                  processing rules.
                </p>
              </div>
            </article>
            <article>
              <span>02</span>
              <div>
                <p className={styles.pathLabel}>Storycards cloud</p>
                <h3>Store data in Storycards when the workflow needs it.</h3>
                <p>
                  Keep collected data in Storycards for platform workflows and analytics. Your organization retains
                  control and can delete the data when it is no longer needed.
                </p>
              </div>
            </article>
          </div>

          <div className={styles.flowLine} aria-label="Storycards data flow">
            <div>
              <span>01</span>
              <strong>Audience interaction</strong>
              <p>People engage with a published or embedded Storycards product.</p>
            </div>
            <div>
              <span>02</span>
              <strong>Controlled collection</strong>
              <p>The product captures the answers and interaction events you configure.</p>
            </div>
            <div>
              <span>03</span>
              <strong>Approved destination</strong>
              <p>Data moves to Storycards or directly into your server, database, CRM or data stack.</p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.controlsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Platform protection</p>
              <h2>Controls across access, traffic and infrastructure.</h2>
            </div>
            <p>
              Layered controls protect the workspace where teams build and the interactive products delivered to your
              audience.
            </p>
          </header>

          <div className={styles.controlGrid}>
            {platformControls.map((control, index) => (
              <article key={control.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{control.title}</h3>
                <p>{control.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.technicalSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Technical assurance</p>
              <h2>How the platform protects infrastructure, access and data.</h2>
            </div>
            <p>
              Security controls extend from the cloud environment and application edge to stored data, recovery and
              ongoing monitoring.
            </p>
          </header>

          <aside className={styles.governedAiCallout} aria-label="Governed AI security model">
            <div>
              <p className={styles.eyebrow}>Governed AI boundary</p>
              <h3>AI that builds with tested components, not generated code.</h3>
            </div>
            <div>
              <p>
                Storycards AI assembles products from production-tested building blocks configured to your design
                system and brand rules. It does not generate or inject its own application code.
              </p>
              <p>
                Every result stays inside the same component system, roles, permissions, review process and publishing
                controls used across the platform.
              </p>
            </div>
          </aside>

          <div className={styles.technicalList}>
            {technicalAssurance.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.assuranceSection}>
        <div className={styles.sectionInner}>
          <header className={styles.assuranceHeader}>
            <div>
              <p className={styles.eyebrow}>Independent assurance</p>
              <h2>Testing and compliance work that supports enterprise review.</h2>
            </div>
            <p>
              Technical controls are supported by independent testing, documented data-processing terms and a
              structured path for customer due diligence.
            </p>
          </header>

          <div className={styles.assuranceGrid}>
            {independentAssurance.map((item) => (
              <article key={item.title}>
                <p className={styles.assuranceLabel}>{item.label}</p>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>

          <p className={styles.assuranceNote}>
            SOC 2 is an independent examination of controls. The Storycards examination is not yet complete, and no SOC
            2 report or certification is claimed on this page.
          </p>
        </div>
      </section>

      <section className={styles.reviewSection}>
        <div className={styles.sectionInner}>
          <header className={styles.reviewHeader}>
            <div>
              <p className={styles.eyebrow}>Security review</p>
              <h2>Bring your requirements. Review the operating model with us.</h2>
            </div>
            <div>
              <p>
                Security requirements vary by organization and use case. Our enterprise team can walk your technical
                stakeholders through the relevant controls and data flow before rollout.
              </p>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.secondaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </header>

          <div className={styles.reviewGrid}>
            {reviewTopics.map((topic) => (
              <article key={topic.title}>
                <h3>{topic.title}</h3>
                <p>{topic.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.faqSection}>
        <div className={styles.sectionInner}>
          <header className={styles.faqHeader}>
            <p className={styles.eyebrow}>Common security questions</p>
            <h2>What enterprise teams ask first.</h2>
          </header>
          <div className={styles.faqList}>
            {questions.map((item, index) => (
              <article key={item.question}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Evaluate Storycards against your security requirements.</h2>
          <div>
            <p>Share your use case, data flow and access requirements with our enterprise team.</p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Plan a security review <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default Security;
